import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import {
  formatPollNotificationItem,
  mergeClientNotificationItems,
  normalizeAdminNotification,
} from "../../../utils/notificationDisplay";
import { pollNeedsResponse } from "../../../utils/clientSessionUtils";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import aa from "../AdminPortal/adminApp.module.css";

const PREVIEW_LIMIT = 8;

const TYPE_ICONS = {
  chat_message: "💬",
  poll: "📋",
  training_reminder: "🏋️",
  session_today: "🏋️",
  session_cancelled: "⚠️",
  session_rescheduled: "🔄",
  membership_expiring: "🎫",
  admin_payment: "💳",
  club_life_post: "📣",
};

function typeIcon(type) {
  return TYPE_ICONS[type] || "🔔";
}

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
}

function chatNotificationLinkProps(href, portal) {
  if (!href) return null;
  const clientChat = href.match(/^\/app\/chat\/personal\/?$/);
  if (clientChat) {
    return { to: "/app/chat/personal" };
  }
  const groupChat = href.match(/^\/app\/chat\/group\/([^/]+)$/);
  if (groupChat) {
    return { to: href };
  }
  const memberChat = href.match(/^\/app\/chat\/member\/([^/]+)$/);
  if (memberChat) {
    return { to: href };
  }
  const staffChat = href.match(/\/clients\/([^/]+)\/chat$/);
  if (staffChat && portal !== "client") {
    return {
      to: href,
      state: { fromChatHub: true },
    };
  }
  const staffGroup = href.match(/\/chat\/group\/([^/]+)$/);
  if (staffGroup && portal !== "client") {
    return {
      to: href,
      state: { fromChatHub: true },
    };
  }
  return { to: href };
}

function PreviewRow({ item, onNavigate, portal }) {
  const href = item.url || item.action_path || item.href;
  const unread = !item.is_read;
  const linkProps = chatNotificationLinkProps(href, portal);
  const rowClass = [
    aa.notifyDropRow,
    unread ? aa.notifyDropRowUnread : "",
    item.tone ? aa[`notifyDropTone_${item.tone}`] || "" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const inner = (
    <>
      <span className={aa.notifyDropRowIcon} aria-hidden>
        {typeIcon(item.type)}
      </span>
      <span className={aa.notifyDropRowBody}>
        <span className={aa.notifyDropRowTop}>
          <span className={aa.notifyDropLabel}>{item.title}</span>
          {item.sent_at ? (
            <time className={aa.notifyDropTime}>{formatWhen(item.sent_at)}</time>
          ) : null}
        </span>
        <span className={aa.notifyDropText}>{item.text}</span>
      </span>
      {unread ? <span className={aa.notifyDropDot} aria-hidden /> : null}
    </>
  );

  if (linkProps) {
    return (
      <Link
        to={linkProps.to}
        state={linkProps.state}
        className={rowClass}
        onClick={onNavigate}
      >
        {inner}
      </Link>
    );
  }

  return <div className={rowClass}>{inner}</div>;
}

export default function NotificationsDropdown({
  open,
  onClose,
  anchorRef,
  allPath,
  portal = "admin",
  athleteId = "",
  onMarkedRead,
}) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const panelRef = useRef(null);
  const loadGenRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pos, setPos] = useState({ top: 56, left: 12, width: 320, arrowLeft: 280 });

  const updatePosition = useCallback(() => {
    if (isMobile) return;
    const el = anchorRef?.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const panelW = Math.min(340, window.innerWidth - 16);
    let left = rect.right - panelW;
    left = Math.max(8, Math.min(left, window.innerWidth - panelW - 8));
    const arrowLeft = Math.min(
      panelW - 18,
      Math.max(18, rect.left + rect.width / 2 - left),
    );
    const panelMaxH = Math.min(420, window.innerHeight * 0.72);
    let top = rect.bottom + 10;
    if (top + panelMaxH > window.innerHeight - 12) {
      top = Math.max(12, window.innerHeight - panelMaxH - 12);
    }
    setPos({
      top,
      left,
      width: panelW,
      arrowLeft,
    });
  }, [anchorRef, isMobile]);

  useLayoutEffect(() => {
    if (!open || isMobile) return undefined;
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, isMobile, updatePosition]);

  useEffect(() => {
    if (!open || !isMobile) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, isMobile]);

  useEffect(() => {
    if (!open) return undefined;

    const gen = ++loadGenRef.current;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        let feed = [];
        if (portal === "client") {
          const params = athleteId ? { athlete_id: athleteId } : {};
          const [notifRes, pollRes] = await Promise.allSettled([
            clinicAPI.getMyNotifications(params),
            clinicAPI.getMyTrainingPolls(params),
          ]);
          if (cancelled || gen !== loadGenRef.current) return;

          const logItems =
            notifRes.status === "fulfilled"
              ? (notifRes.value.notifications || []).map(normalizeAdminNotification)
              : [];
          const pollItems =
            pollRes.status === "fulfilled"
              ? (pollRes.value.polls || [])
                  .filter(pollNeedsResponse)
                  .map(formatPollNotificationItem)
              : [];
          feed = mergeClientNotificationItems(logItems, pollItems).slice(
            0,
            PREVIEW_LIMIT,
          );
        } else {
          const data = await clinicAPI.getMyAdminNotifications();
          if (cancelled || gen !== loadGenRef.current) return;
          feed = (data.notifications || [])
            .map(normalizeAdminNotification)
            .slice(0, PREVIEW_LIMIT);
        }

        setItems(feed);

        if (portal === "client") {
          const params = athleteId ? { athlete_id: athleteId } : {};
          await clinicAPI.markMyNotificationsRead(params);
        } else {
          await clinicAPI.markMyAdminNotificationsRead();
        }

        if (!cancelled && gen === loadGenRef.current) {
          setItems((prev) => prev.map((item) => ({ ...item, is_read: true })));
          onMarkedRead?.();
        }
      } catch {
        if (!cancelled && gen === loadGenRef.current) setItems([]);
      } finally {
        if (!cancelled && gen === loadGenRef.current) setLoading(false);
      }
    };

    load();

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    const onPointer = (e) => {
      const anchor = anchorRef?.current;
      const panel = panelRef.current;
      if (panel?.contains(e.target) || anchor?.contains(e.target)) return;
      onClose?.();
    };

    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      cancelled = true;
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open, portal, athleteId, onClose, anchorRef, onMarkedRead]);

  const goAll = () => {
    onClose?.();
    navigate(allPath);
  };

  const onRowNavigate = () => {
    onClose?.();
  };

  if (!open) return null;

  const panelStyle = isMobile
    ? undefined
    : {
        top: pos.top,
        left: pos.left,
        width: pos.width,
        "--notify-arrow-left": `${pos.arrowLeft}px`,
      };

  const listBody = loading ? (
    <div className={aa.notifyDropEmpty}>
      <span className={aa.notifyDropSpinner} aria-hidden />
      Загрузка…
    </div>
  ) : items.length === 0 ? (
    <div className={aa.notifyDropEmpty}>
      <span className={aa.notifyDropEmptyIcon} aria-hidden>
        ✓
      </span>
      Нет новых уведомлений
    </div>
  ) : (
    <ul className={aa.notifyDropList}>
      {items.map((item) => (
        <li key={item.id || `${item.type}:${item.text}`}>
          <PreviewRow item={item} onNavigate={onRowNavigate} portal={portal} />
        </li>
      ))}
    </ul>
  );

  if (isMobile) {
    return createPortal(
      <div
        className={aa.notifyDropSheetBackdrop}
        onClick={() => onClose?.()}
        role="presentation"
      >
        <div
          ref={panelRef}
          className={aa.notifyDropSheet}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label="Уведомления"
        >
          <div className={aa.notifyDropSheetHandle} aria-hidden />
          <header className={aa.notifyDropHead}>
            <h2 className={aa.notifyDropTitle}>Уведомления</h2>
            <button
              type="button"
              className={aa.notifyDropSheetClose}
              onClick={() => onClose?.()}
              aria-label="Закрыть"
            >
              <svg viewBox="0 0 24 24" aria-hidden>
                <path
                  d="M6 6l12 12M18 6 6 18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </header>
          <div className={aa.notifyDropBody}>{listBody}</div>
          <footer className={aa.notifyDropFoot}>
            <button type="button" className={aa.notifyDropAllBtn} onClick={goAll}>
              Показать все
            </button>
          </footer>
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div className={aa.notifyDropRoot} role="presentation">
      <button
        type="button"
        className={aa.notifyDropScrim}
        aria-label="Закрыть уведомления"
        onClick={() => onClose?.()}
      />
      <div
        ref={panelRef}
        className={aa.notifyDropPanel}
        style={panelStyle}
        role="dialog"
        aria-label="Уведомления"
      >
        <header className={aa.notifyDropHead}>
          <h2 className={aa.notifyDropTitle}>Уведомления</h2>
        </header>
        <div className={aa.notifyDropBody}>{listBody}</div>
        <footer className={aa.notifyDropFoot}>
          <button type="button" className={aa.notifyDropAllBtn} onClick={goAll}>
            Показать все
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
